// ────────────────────────────────────────────────────────────────────────────
// The evaluator, and the samples it runs against.
//
// This is deliberately a PURE function over (subject, requirements): exactly the
// shape the backend needs, and for the same reason the existing
// `InvoiceApprovalEvaluator` is pure — one implementation has to serve both the
// screen (explain why the button is disabled) and the command (refuse the move).
// Two copies would drift, and the symptom would be a UI that permits what the
// server rejects.
//
// Written here in the prototype so the rules can be felt before they are built.
// ────────────────────────────────────────────────────────────────────────────
import { resolveOrderReferenceLabel } from '@/utils/orderReferenceLabels';
import {
  polarityOf,
  requirementMeta,
  type PaymentTerms,
  type Requirement,
  type RequirementKind,
  type Subject,
  type WorkflowTransition,
} from './mockWorkflow';

/** The bits of an order the rules look at. */
export interface SampleOrder {
  kind: 'order';
  id: string;
  ref: string;
  party: string;
  /** Reference slots 1-4, as the org labels them. */
  references: (string | null)[];
  tasks: {
    type: string;
    state: 'pending' | 'inProgress' | 'completed' | 'cancelled';
    plannedDate: string | null;
    cargoItems: number;
    podSigned: boolean;
  }[];
  chargeLines: { net: number }[];
  /** Billing terms + whether the customer's invoice is settled (drives `customerPaid`). */
  paymentTerms: PaymentTerms;
  invoicePaid: boolean;
  /** A customer invoice exists against this order — blocks cancellation. */
  invoiced: boolean;
  /** A cost on this job has been settled with a supplier — blocks cancellation. */
  supplierPaid: boolean;
}

/** The bits of a trip (a load) the rules look at. */
export interface SampleTrip {
  kind: 'trip';
  id: string;
  ref: string;
  party: string;
  lines: number;
  plannedDate: string | null;
  driver: string | null;
  vehicle: string | null;
  legs: { state: 'pending' | 'inProgress' | 'completed'; podSigned: boolean }[];
  /** Billing terms + whether the carried order's invoice is settled. */
  paymentTerms: PaymentTerms;
  invoicePaid: boolean;
  /** A cost on this trip has been settled with a supplier. */
  supplierPaid: boolean;
}

export type Sample = SampleOrder | SampleTrip;

/**
 * Which task types count as collecting / delivering — every family's words
 * for the two roles (pallets Collect / Deliver, containers Pick-up / Drop-off,
 * tanks Load / Unload). `Drop` here used to match nothing, so a Drop-off or an
 * Unload never counted as a delivery.
 */
const COLLECTING = ['Collect', 'PickUp', 'Load'];
const DELIVERING = ['Deliver', 'DropOff', 'Unload'];

export interface Unmet {
  kind: RequirementKind;
  severity: Requirement['severity'];
  /** What is actually wrong, in the words an operator would use. */
  reason: string;
}

/**
 * Unmet requirements for one transition, worst first.
 *
 * `ignore` requirements are skipped entirely — switched off means not evaluated,
 * not evaluated-and-hidden, so turning a rule off can never cost anything.
 */
export function evaluate(
  sample: Sample,
  transition: WorkflowTransition,
  /**
   * The organisation's reference labels, so an unmet condition can name the
   * field the way the operator does. Only used for wording — the rule itself
   * is stored against a slot NUMBER, which survives a rename.
   */
  referenceLabels: string[] = [],
): Unmet[] {
  const out: Unmet[] = [];

  for (const req of transition.requirements) {
    if (req.severity === 'ignore') continue;

    // `check` answers one question — does the fact HOLD? — and returns why not
    // when it doesn't. A `require` rule is unmet when the fact is missing; a
    // `prevent` rule is unmet when the fact is PRESENT, which is the same call
    // read the other way round.
    const missing = check(sample, req, transition, referenceLabels);
    if (polarityOf(req) === 'require') {
      if (missing) out.push({ kind: req.kind, severity: req.severity, reason: missing });
    } else if (!missing) {
      const meta = requirementMeta(req.kind);
      out.push({
        kind: req.kind,
        severity: req.severity,
        // Positive wording: negating the require-direction reason would give
        // "No charge lines" for an order that plainly HAS them.
        reason: meta.preventReason ?? meta.label,
      });
    }
  }

  // Blocking first: an operator reads the top line and stops.
  return out.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'stop' ? -1 : 1));
}

/** Null when satisfied, otherwise the reason it is not. */
function check(
  sample: Sample,
  req: Requirement,
  transition: WorkflowTransition,
  referenceLabels: string[],
): string | null {
  // State checks are answered from the MOVE, not the record: the transition's
  // `from` is where the record currently sits, and the samples deliberately
  // carry facts rather than a state so one sample can be tried against every
  // move on the track.
  if (req.kind === 'fromState') {
    const states = (req.param ?? '').split(',').map((x) => x.trim()).filter(Boolean);
    return states.includes(transition.from) ? null : 'Not in one of those states';
  }
  if (sample.kind === 'order') return checkOrder(sample, req, referenceLabels);
  return checkTrip(sample, req);
}

function checkOrder(
  order: SampleOrder,
  req: Requirement,
  referenceLabels: string[],
): string | null {
  switch (req.kind) {
    case 'customerReference': {
      // The param is the 1-based SLOT, not the label. Storing the label would
      // break the rule the moment an organisation renamed the field: the
      // lookup would match nothing, and a Block would then refuse every order.
      const slot = Number(req.param ?? 1);
      const value = Number.isFinite(slot) && slot >= 1 ? order.references[slot - 1] : null;
      return value && value.trim()
        ? null
        : `${resolveOrderReferenceLabel(referenceLabels, slot - 1)} is empty`;
    }
    case 'minTasks': {
      const min = Number(req.param ?? 1);
      return order.tasks.length >= min
        ? null
        : `${order.tasks.length} task${order.tasks.length === 1 ? '' : 's'}, needs ${min}`;
    }
    case 'hasCollection':
      return order.tasks.some((t) => COLLECTING.includes(t.type)) ? null : 'No collection task';
    case 'hasDelivery':
      return order.tasks.some((t) => DELIVERING.includes(t.type)) ? null : 'No delivery task';
    case 'charges':
      return order.chargeLines.some((c) => c.net !== 0) ? null : 'No charge lines';
    case 'plannedDates': {
      const missing = order.tasks.filter((t) => !t.plannedDate).length;
      return missing === 0 ? null : `${missing} task${missing === 1 ? '' : 's'} with no planned date`;
    }
    case 'cargo':
      return order.tasks.some((t) => COLLECTING.includes(t.type) && t.cargoItems > 0)
        ? null
        : 'No cargo entered';
    case 'allTasksFinished': {
      const open = order.tasks.filter((t) => t.state === 'pending' || t.state === 'inProgress').length;
      return open === 0 ? null : `${open} task${open === 1 ? '' : 's'} still open`;
    }
    case 'podSigned': {
      // A cancelled delivery was never made, so it owes no POD — the same rule
      // as the invoicing POD check (`InvoiceApprovalEvaluator`).
      const outstanding = order.tasks.filter(
        (t) => DELIVERING.includes(t.type) && t.state !== 'cancelled' && !t.podSigned,
      ).length;
      return outstanding === 0
        ? null
        : `${outstanding} ${outstanding === 1 ? 'delivery' : 'deliveries'} without a POD`;
    }
    case 'customerPaid':
      // Only cash (prepaid) orders must be paid before work starts; for credit /
      // self-billed / internal terms the rule doesn't apply, so it's satisfied.
      // NOTE: this is exactly why the check is require-only — inverting it
      // would read a credit customer's "not applicable" as "has paid".
      return order.paymentTerms !== 'cash' || order.invoicePaid
        ? null
        : 'Cash order — invoice not yet paid';
    // Plain facts, with no "does the rule apply" logic folded in — which is
    // what makes them safe to read in the `prevent` direction.
    case 'invoiced':
      return order.invoiced ? null : 'Not invoiced';
    case 'paymentReceived':
      return order.invoicePaid ? null : 'No payment received';
    case 'supplierPaid':
      return order.supplierPaid ? null : 'No supplier paid';
    default:
      return 'Not applicable to an order';
  }
}

function checkTrip(trip: SampleTrip, req: Requirement): string | null {
  switch (req.kind) {
    case 'hasLines': {
      const min = Number(req.param ?? 1);
      return trip.lines >= min ? null : `${trip.lines} cargo lines, needs ${min}`;
    }
    case 'plannedDates':
      return trip.plannedDate ? null : 'No planned date';
    case 'hasDriver':
      return trip.driver ? null : 'No driver in the Driver slot';
    case 'hasVehicle':
      return trip.vehicle ? null : 'No vehicle assigned';
    case 'allLegsCompleted': {
      const open = trip.legs.filter((l) => l.state !== 'completed').length;
      return open === 0
        ? null
        : `${open} of ${trip.legs.length} leg${trip.legs.length === 1 ? '' : 's'} not completed`;
    }
    case 'podSigned': {
      const missing = trip.legs.filter((l) => !l.podSigned).length;
      return missing === 0 ? null : `${missing} leg${missing === 1 ? '' : 's'} without a POD`;
    }
    case 'customerPaid':
      return trip.paymentTerms !== 'cash' || trip.invoicePaid
        ? null
        : 'Cash order on this trip — invoice not yet paid';
    case 'paymentReceived':
      return trip.invoicePaid ? null : 'No payment received';
    case 'workStarted':
      return trip.legs.some((l) => l.state !== 'pending') ? null : 'No leg started';
    case 'supplierPaid':
      return trip.supplierPaid ? null : 'No supplier paid';
    default:
      return 'Not applicable to a trip';
  }
}

/** Blocked when anything at stop level is unmet. Warnings pass through. */
export function isBlocked(unmet: Unmet[]): boolean {
  return unmet.some((u) => u.severity === 'stop');
}

/**
 * Samples per subject, chosen so all three outcomes are visible: clean, blocked,
 * and allowed-with-a-warning. That last one is the case worth checking against a
 * real design — a warning that reads like an error trains people to ignore both.
 */
const SAMPLE_ORDERS: SampleOrder[] = [
  {
    kind: 'order',
    id: 'o1',
    ref: 'ORD-10241',
    party: 'Kilkenny Cold Chain',
    paymentTerms: 'credit',
    invoicePaid: false,
    invoiced: false,
    supplierPaid: false,
    references: ['CUST-REF-88213', 'JOB-5120', null, null],
    tasks: [
      { type: 'Collect', state: 'completed', plannedDate: '2026-08-13', cargoItems: 12, podSigned: false },
      { type: 'Deliver', state: 'completed', plannedDate: '2026-08-14', cargoItems: 0, podSigned: true },
    ],
    chargeLines: [{ net: 780 }, { net: 66.3 }],
  },
  {
    kind: 'order',
    id: 'o2',
    ref: 'ORD-10262',
    party: 'Abbott Ireland',
    paymentTerms: 'cash',
    invoicePaid: false,
    invoiced: false,
    supplierPaid: true,
    references: [null, null, null, null],
    tasks: [
      { type: 'Collect', state: 'completed', plannedDate: null, cargoItems: 0, podSigned: false },
      { type: 'Deliver', state: 'pending', plannedDate: null, cargoItems: 0, podSigned: false },
    ],
    chargeLines: [],
  },
  {
    kind: 'order',
    id: 'o3',
    ref: 'ORD-10299',
    party: 'Arla Foods UK',
    paymentTerms: 'cash',
    invoicePaid: true,
    invoiced: true,
    supplierPaid: false,
    references: ['CUST-REF-90112', null, null, null],
    tasks: [
      { type: 'Load', state: 'completed', plannedDate: '2026-08-27', cargoItems: 4, podSigned: false },
      { type: 'Unload', state: 'completed', plannedDate: '2026-08-27', cargoItems: 0, podSigned: true },
    ],
    chargeLines: [{ net: 1240 }],
  },
];

const SAMPLE_TRIPS: SampleTrip[] = [
  {
    kind: 'trip',
    id: 't1',
    ref: 'MAN-000418',
    party: 'Dublin depot · 13.6m curtain',
    paymentTerms: 'credit',
    invoicePaid: false,
    supplierPaid: true,
    lines: 8,
    plannedDate: '2026-08-27',
    driver: 'J. Byrne',
    vehicle: '191-D-4471',
    legs: [
      { state: 'completed', podSigned: true },
      { state: 'completed', podSigned: true },
    ],
  },
  {
    kind: 'trip',
    id: 't2',
    ref: 'MAN-000419',
    party: 'Dublin depot · unassigned',
    paymentTerms: 'cash',
    invoicePaid: false,
    supplierPaid: false,
    lines: 3,
    plannedDate: '2026-08-28',
    driver: null,
    vehicle: null,
    legs: [
      { state: 'pending', podSigned: false },
      { state: 'pending', podSigned: false },
    ],
  },
  {
    kind: 'trip',
    id: 't3',
    ref: 'MAN-000420',
    party: 'Cork depot · part run',
    paymentTerms: 'credit',
    invoicePaid: false,
    supplierPaid: false,
    lines: 5,
    plannedDate: '2026-08-27',
    driver: 'M. Walsh',
    vehicle: null,
    legs: [
      { state: 'completed', podSigned: true },
      { state: 'inProgress', podSigned: false },
    ],
  },
];

/**
 * Samples for a subject. Legs, charges and costs reuse the trip and order
 * samples where the shape fits, and return nothing where it does not — better an
 * honest empty panel than a preview quietly evaluating the wrong thing.
 */
export function samplesFor(subject: Subject): Sample[] {
  switch (subject) {
    case 'order':
      return SAMPLE_ORDERS;
    case 'trip':
      return SAMPLE_TRIPS;
    default:
      return [];
  }
}
